import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AUTH_TOKEN_KEY } from "../shared";

function useTokenFromLocalStorage(): {
  token: string | null;
  setToken: (newToken: string) => void;
  wipeToken: () => void;
} {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem(AUTH_TOKEN_KEY)
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setToken(localStorage.getItem(AUTH_TOKEN_KEY));
    }, 250);
    return () => window.clearTimeout(timeoutId);
  });

  return {
    token,
    setToken: (newToken: string) => {
      localStorage.setItem(AUTH_TOKEN_KEY, newToken);
      setToken(newToken);
    },
    wipeToken: () => {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setToken(null);
    },
  };
}

interface AuthContextProps {
  user: string | null;
  isLoggedIn: boolean;
  storeTokenUserAndRedirect: (token: string) => void;
  wipeTokenAndRedirect: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token, setToken, wipeToken } = useTokenFromLocalStorage();

  const user: string | null = useMemo(() => {
    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.sub;
    }
    return null;
  }, [token]);

  const isLoggedIn: boolean = useMemo(() => !!user, [user]);

  const storeTokenUserAndRedirect = (newToken: string) => {
    setToken(newToken);
  };

  const wipeTokenAndRedirect = () => {
    wipeToken();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        storeTokenUserAndRedirect,
        wipeTokenAndRedirect,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
